<ng-container *ngIf="nuggetAlphaFlow$ | async; then nuggetAlpha; else enhancedPublish"> </ng-container>
<ng-template #nuggetAlpha>
  <div class="title" mat-dialog-title>
    <mode-dialog-header>
      <h1 heading>Publish to ThoughtSpot</h1>
    </mode-dialog-header>
  </div>
  <section class="body">
    <p>This makes the data available as a table in ThoughtSpot’s Mode Connection.</p>
    <ng-container *ngIf="!notebookDatasetPublishUpdatesEnabled; else publishClearanceGuidanceTemplate">
      <form class="section email-form" [formGroup]="form">
        <div class="form-field">
          <div class="flex-center">
            <label class="mode-label" for="dataset-name"> Dataset name </label>
            <capra-status-icon
              class="info-icon"
              status="info"
              capraTooltip
              [tooltipContent]="nameNormalizationInfo"
              tooltipPlacement="right"
              tooltipSize="regular"
            ></capra-status-icon>
          </div>
          <input
            data-test="publish-dataset-name-input"
            id="dataset-name"
            class="mode-input"
            [class.mode-input-error]="form.nameErrors"
            formControlName="name"
            type="text"
            capraAutofocus
          />
          <div *ngIf="form.nameErrors as errors">
            <div class="error-msg" *ngFor="let errorKey of form.errorKeys(errors)">
              <span>{{ errors[errorKey] }}</span>
            </div>
          </div>
        </div>
      </form>
    </ng-container>
  </section>
  <mode-status-banner status="info" show-icon="true" accent="blue" layout="full-width">
    <span
      >After the table is imported into ThoughtSpot, the most recent results will be used automatically. Changes to the
      Dataset’s structure will require manual updates to the table and may break content built on it.</span
    >
  </mode-status-banner>
</ng-template>
<ng-template #enhancedPublish>
  <div class="title" mat-dialog-title>
    <mode-dialog-header>
      <h1 heading>Publish to the Data workspace</h1>
    </mode-dialog-header>
  </div>
  <section class="body">
    <p class="sub-text">
      This imports the dataset as a table into the Data workspace. The table will only be visible to you and admins by
      default.
    </p>
    <ng-container *ngIf="!notebookDatasetPublishUpdatesEnabled; else publishClearanceGuidanceTemplate">
      <form class="section email-form" [formGroup]="form">
        <div class="form-field">
          <div class="flex-center">
            <label class="mode-label" for="dataset-name"> Dataset name </label>
            <capra-status-icon
              class="info-icon"
              status="info"
              capraTooltip
              [tooltipContent]="nameNormalizationInfo"
              tooltipPlacement="right"
              tooltipSize="regular"
            ></capra-status-icon>
          </div>
          <input
            data-test="publish-dataset-name-input"
            id="dataset-name"
            class="mode-input"
            [class.mode-input-error]="form.nameErrors"
            formControlName="name"
            type="text"
            capraAutofocus
          />
          <div *ngIf="form.nameErrors as errors">
            <div class="error-msg" *ngFor="let errorKey of form.errorKeys(errors)">
              <span>{{ errors[errorKey] }}</span>
            </div>
          </div>
        </div>
      </form>
    </ng-container>
    <div class="dataset-details">
      <p class="mode-label">Dataset details</p>
      <mode-dataset-metadata
        *ngIf="enhancedDatasetResult$ | async as dataset"
        [isSheetDataset]="(isSheetDataset$ | async) ?? false"
        [isNotebookCell]="(isNotebookCell$ | async) ?? false"
        [hasCalcFields]="(hasCalcFields$ | async) ?? false"
        [rowCount]="dataset.rowCount ?? 0"
        [columnCount]="dataset?.metaColumns?.length"
        [size]="dataset.bytes ?? 0"
        [connection]="(datasourceDisplayName$ | async) ?? ''"
        [collection]="(dataset$ | async)?.details?.collection?.name ?? 'Personal'"
        [schedules]="(schedules$ | async) ?? ''"
        (addScheduleClick)="openScheduleModal()"
      ></mode-dataset-metadata>
    </div>
  </section>
  <mode-status-banner status="info" show-icon="true" accent="blue" layout="full-width">
    <span *ngIf="(datasetRepublishing$ | async) === false">
      The imported table will automatically use the most recent results available. Changes to the dataset’s structure
      will require manual updates to the table and may break content built on it.</span
    >
    <span *ngIf="datasetRepublishing$ | async"
      >The table will automatically display the most recent data. Changes to the published dataset structure will be auto-reflected in the data workspace and could affect any content that relies on it. After publishing, avoid renaming the dataset or changing its collection to prevent disruptions.
    </span>
  </mode-status-banner>
</ng-template>

<div mat-dialog-actions>
  <capra-button
    class="cancel-btn"
    text="Cancel"
    data-test="cancel-feedback"
    variant="secondary"
    color="neutral"
    modeTrackClick
    [trackClickData]="{ location: 'Publish dataset modal', action: 'Publish dataset close CTA' }"
    (clicked)="close()"
  ></capra-button>
  <capra-button
    text="Publish"
    data-test="send-feedback"
    [disabled]="!form.valid"
    modeTrackClick
    [trackClickData]="{ location: 'Publish dataset modal', action: 'Publish dataset CTA' }"
    (clicked)="publishDataset()"
  ></capra-button>
</div>
<ng-template #publishClearanceGuidanceTemplate>
  <form class="section email-form" [formGroup]="form">
    <div class="form-field">
      <div class="flex-center">
        <label class="mode-label" for="table-name"> Table name </label>
        <capra-status-icon
          class="info-icon"
          status="info"
          capraTooltip
          [tooltipContent]="'Used as the default display name for the imported table and when editing its TML'"
          tooltipPlacement="right"
          tooltipSize="regular"
        ></capra-status-icon>
      </div>
      <input
        data-test="publish-table-name-input"
        id="table-name"
        class="mode-input"
        [class.mode-input-error]="form.nameErrors"
        formControlName="name"
        type="text"
        capraAutofocus
      />
      <div *ngIf="form.nameErrors as errors">
        <div class="error-msg" *ngFor="let errorKey of form.errorKeys(errors)">
          <span>{{ errors[errorKey] }}</span>
        </div>
      </div>
    </div>
    <div class="form-field">
      <div class="flex-center">
        <label class="mode-label" for="table-schema"> Table schema </label>
        <capra-status-icon
          class="info-icon"
          status="info"
          capraTooltip
          [tooltipContent]="
            'Auto-generated based on the Collection your data is published from. Table names within a single Collection must be unique'
          "
          tooltipPlacement="right"
          tooltipSize="regular"
        ></capra-status-icon>
      </div>
      <input
        data-test="publish-table-schema-input"
        id="table-schema"
        class="mode-input"
        formControlName="schema"
        type="text"
      />
    </div>
  </form>
</ng-template>
